@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.yard

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.example.project.service.ZustandManager
import org.example.project.enginetabular.CellColor
import org.example.project.enginetabular.TabularScreen
import org.example.project.service.YardApi
import org.example.project.service.YardLiveSync
import org.example.project.utils.All
import org.jetbrains.compose.web.css.Color

@Composable
fun WEAuskunftScreen() {
    val scope = rememberCoroutineScope()
    var eintraege by remember { mutableStateOf<List<All>>(emptyList()) }
    var lkwEintraege by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedKopfKey by remember { mutableStateOf<String?>(null) }
    var kontrolleDefs by remember { mutableStateOf<List<All>>(emptyList()) }
    var searchedKopf by remember { mutableStateOf<List<All>?>(emptyList()) }
    var searchedDetails by remember { mutableStateOf<List<All>?>(emptyList()) }
    val refreshVersion = YardLiveSync.version()

    LaunchedEffect(refreshVersion) {
        searchedKopf = emptyList()
        searchedDetails = emptyList()
        eintraege = YardApi.loadWeListe()
        lkwEintraege = YardApi.loadWeLkwListe()
        kontrolleDefs = YardApi.loadKontrolleDefinitionen()
            .filter { it.aktivWe }
    }

    val headersTop = listOf(
        "ID", "Datum", "Angemeldet Uhrzeit", "lieferscheinnummer",
        "Lieferant", "Unternehmer", "Paletten Eingang", "Paletten Ausgang",
        "Paletten unbeschädigt", "Fehlmenge", "Bruch", "Überzählig",
        "Zustand", "Standort", "Tor", "Abgeschlossen Uhrzeit"
    )

    val zustandIdxTop = headersTop.indexOf("Zustand")
    val cellColorsTop = eintraege.associate {
        (weAuskunftKopfRowId(it) to zustandIdxTop) to
                CellColor(Color(ZustandManager.colorHexFor(it.zustand)))
    }

    TabularScreen(
        screenKey = "WEAuskunftScreen_Kopf",
        rollenKey = "WEAuskunftScreen",
        title = "Wareneingang - Auskunft",
        headers = headersTop,
        models = (searchedKopf ?: eintraege).map { it.copy(id = weAuskunftKopfRowId(it)) },
        data = (searchedKopf ?: eintraege).map { weAuskunftKopfRowId(it) to mapWeKontrolleKopfRow(it, lkwEintraege) },
        cellBackgroundColors = cellColorsTop,
        nonEditableCols = headersTop.toSet(),
        editable = false,
        includeMetadata = true,
        onRowClick = { rowId ->
            selectedKopfKey = (searchedKopf ?: eintraege)
                .find { weAuskunftKopfRowId(it) == rowId }
                ?.let(::weKopfDetailKey)
            searchedDetails = null
        },
        onSearchWithFilters = { filters ->
            scope.launch { searchedKopf = YardApi.searchWeAuskunft(filters, "kopf") }
        },
        onCommit = {}
    )

    val details = remember(selectedKopfKey, lkwEintraege) {
        val key = selectedKopfKey.orEmpty()
        if (key.isBlank()) emptyList()
        else lkwEintraege.filter { weKopfDetailKey(it) == key }
    }

    if (details.isNotEmpty()) {
        WEKontrolleDetailsTable(
            searchedDetails ?: details, kontrolleDefs,
            onSearch = { filters ->
                scope.launch {
                    searchedDetails = YardApi.searchWeAuskunft(filters, "detail", selectedKopfKey.orEmpty())
                }
            }
        )
    }
}

private fun mapWeKontrolleKopfRow(entry: All, lkwEintraege: List<All>): List<String> {
    val lkw = lkwEintraege.filter { weKopfDetailKey(it) == weKopfDetailKey(entry) }
    val palettenEingang = lkw.sumOf { weAbschlussIstPaletten(it) }
    val hatAbschluss = lkw.any { weAbschlussBestaetigt(it) }
    val leerpaletten = lkw.sumOf { if (weAbschlussBestaetigt(it)) it.leerpalettenanzahl else 0 }
    val unbeschaedigt = lkw.sumOf { weAbschlussExtraInt(it, it.mobilWePalettenUnbeschaedigt) }
    val fehlmenge = lkw.sumOf { weAbschlussExtraInt(it, it.mobilWePalettenFehlmenge) }
    val bruch = lkw.sumOf { weAbschlussExtraInt(it, it.mobilWePalettenBruch) }
    val ueberzaehlig = lkw.sumOf { weAbschlussExtraInt(it, it.mobilWePalettenUeberzaehlig) }
    return listOf(
        weAuftragKopfId(entry),
        entry.datum,
        entry.uhrzeitAnmeldung,
        entry.lieferscheinnummer,
        entry.lieferant,
        entry.unternehmer,
        palettenEingang.takeIf { it > 0 }?.toString().orEmpty(),
        if (hatAbschluss) leerpaletten.toString() else "",
        unbeschaedigt.takeIf { it > 0 }?.toString().orEmpty(),
        fehlmenge.takeIf { it > 0 }?.toString().orEmpty(),
        bruch.takeIf { it > 0 }?.toString().orEmpty(),
        ueberzaehlig.takeIf { it > 0 }?.toString().orEmpty(),
        "${entry.zustand} - ${weZustandText(entry.zustand)}",
        entry.standort,
        entry.tor?.toString().orEmpty(),
        entry.uhrzeitAbgeschlossen
    )
}

private fun weAuftragKopfId(entry: All): String =
    entry.id.ifBlank { entry.lfdNr.toString() }

private fun weKopfDetailKey(entry: All): String =
    entry.lieferscheinnummer.ifBlank { entry.id.ifBlank { entry.lfdNr.toString() } }

private fun weAuskunftKopfRowId(entry: All): String =
    listOf(
        weAuftragKopfId(entry),
        entry.lieferscheinnummer,
        entry.lfdNr.takeIf { it > 0 }?.toString().orEmpty()
    )
        .filter { it.isNotBlank() }
        .joinToString("|")
        .ifBlank { "we-kopf-${entry.hashCode()}" }

private fun weAuskunftLkwRowId(entry: All): String =
    listOf(
        weKopfDetailKey(entry),
        entry.id,
        entry.lfdNr.takeIf { it > 0 }?.toString().orEmpty(),
        entry.lkwkennzeichen,
        entry.aufliegerkennzeichen
    )
        .filter { it.isNotBlank() }
        .joinToString("|")
        .ifBlank { "we-lkw-${entry.hashCode()}" }

private val baseHeadersWe = listOf(
    "PIN", "PIN bestätigt", "Datum", "Angemeldet Uhrzeit", "Zustand",
    "lieferscheinnummer", "Lieferant", "Unternehmer", "Auftraggeber",
    "Fahrer", "LKW", "Auflieger",
    "Standort", "Tor", "Zuweisung Uhrzeit", "Abgeschlossen Uhrzeit",
    "Paletten Eingang", "Paletten Ausgang",
    "Paletten unbeschädigt", "Fehlmenge", "Bruch", "Überzählig"
)

@Composable
private fun WEKontrolleDetailsTable(
    lkw: List<All>,
    defs: List<All>,
    onSearch: (Map<String, String>) -> Unit
) {
    val headers = baseHeadersWe + defs.map { it.kontrollBezeichnung }
    val zustandIdx = baseHeadersWe.indexOf("Zustand")
    val cellColors = lkw.associate {
        (weAuskunftLkwRowId(it) to zustandIdx) to
                CellColor(Color(ZustandManager.colorHexFor(it.zustand)))
    }

    TabularScreen(
        screenKey = "WEAuskunftScreen_LKW",
        rollenKey = "WEAuskunftScreen",
        title = "LKW-Details - WE Auskunft",
        headers = headers,
        models = lkw.map { it.copy(id = weAuskunftLkwRowId(it)) },
        data = lkw.map { weAuskunftLkwRowId(it) to mapWeKontrolleLkwRow(it, defs) },
        cellBackgroundColors = cellColors,
        toggleColumns = setOf("PIN bestätigt"),
        nonEditableCols = headers.toSet(),
        editable = false,
        includeMetadata = true,
        onSearchWithFilters = onSearch,
        onCommit = {}
    )
}

private fun mapWeKontrolleLkwRow(entry: All, defs: List<All>): List<String> {
    val base = listOf(
        yardPinCode(entry),
        yardPinBestaetigtLabel(entry),
        entry.datum,
        entry.uhrzeitAnmeldung,
        "${entry.zustand} - ${weZustandText(entry.zustand)}",
        entry.lieferscheinnummer,
        entry.lieferant,
        entry.unternehmer,
        entry.auftraggeber,
        entry.fahrername,
        entry.lkwkennzeichen,
        entry.aufliegerkennzeichen,
        entry.standort,
        entry.tor?.toString().orEmpty(),
        entry.uhrzeitTorvergabe,
        entry.uhrzeitAbgeschlossen,
        weAbschlussIstPaletten(entry).takeIf { it > 0 }?.toString().orEmpty(),
        if (weAbschlussBestaetigt(entry)) entry.leerpalettenanzahl.toString() else "",
        weAbschlussExtraInt(entry, entry.mobilWePalettenUnbeschaedigt).takeIf { it > 0 }?.toString().orEmpty(),
        weAbschlussExtraInt(entry, entry.mobilWePalettenFehlmenge).takeIf { it > 0 }?.toString().orEmpty(),
        weAbschlussExtraInt(entry, entry.mobilWePalettenBruch).takeIf { it > 0 }?.toString().orEmpty(),
        weAbschlussExtraInt(entry, entry.mobilWePalettenUeberzaehlig).takeIf { it > 0 }?.toString().orEmpty()
    )

    val kontrolleValues = defs.map { def -> weKontrollValue(entry, def) }

    return base + kontrolleValues
}

private fun weAbschlussBestaetigt(entry: All): Boolean =
    entry.mobilPalettenAbgeladenBestaetigt

private fun weAbschlussIstPaletten(entry: All): Int =
    if (weAbschlussBestaetigt(entry)) {
        entry.yardabschlussIstPaletten.takeIf { it > 0 }
            ?: (entry.mobilWePalettenUnbeschaedigt + entry.mobilWePalettenBruch).takeIf { it > 0 }
            ?: entry.palettenanzahl
    } else {
        0
    }

private fun weAbschlussExtraInt(entry: All, value: Int): Int =
    if (weAbschlussBestaetigt(entry)) value else 0

private fun weZustandText(zustand: Int): String =
    when (zustand) {
        ZustandManager.IN_VERLADUNG -> "In Entladung"
        ZustandManager.TEILENTLADEN -> "Teilentladen"
        else -> ZustandManager.textFor(zustand)
    }

private fun weKontrollValue(entry: All, def: All): String =
    normalizeKontrollAnzeige(
        entry.palettenWerte["mobil.kontrolle.${def.id.ifBlank { def.kontrollId }}"].orEmpty()
        .ifBlank { entry.palettenWerte[def.kontrollBezeichnung].orEmpty() }
        .ifBlank { entry.palettenWerte[def.bezeichnung].orEmpty() },
        def
    )

private fun normalizeKontrollAnzeige(value: String, def: All): String {
    if (!def.kontrollTyp.equals("checkbox", true)) return value
    return when (value.trim().lowercase()) {
        "\u2705", "ja", "true", "1" -> "\u2705"
        "\u2B1C", "nein", "false", "0" -> "\u2B1C"
        else -> value
    }
}

